import React, { useRef, useState, ReactNode, useEffect } from "react";
import type * as types from "../PhoenixFormsV4";
import type * as appData from "../PhoenixFormsV4.AppData";
import type * as models from "../PhoenixFormsV4.Models";
import { Button, TextField } from "react-md";
import { usePhoenixFormsDialog } from "./usePhoenixFormsDialog";
import { MSelect } from "components/Shared";


const ButtonRow = ({
  children,
}: React.PropsWithChildren) => {

  const dialog  = usePhoenixFormsDialog();

  return <div className="button-row">
    <div className="left">
      {children}
    </div>
    <div className="right">
      <Button
        raised
        onClick={() => dialog.hide()}
      >
        Cancel
      </Button>
    </div>
  </div>;
};

interface WizardProps {
  id: number;
  photoData: models.PhotoControlData;
  proxy: types.PhoenixFormsProxy;
  webRuntimeData: appData.WebRuntimeAppData;
};

const Preview = ({
  id,
  photoData,
  proxy,
  webRuntimeData,
} : WizardProps) => {

  const dialog  = usePhoenixFormsDialog();

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  const photoId = photoData.photos[currentPhotoIndex].id;
  const imageUrl = `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/photos/${photoId}`;

  const addAnother = () => {
    dialog.hide();
    dialog.show(<Chooser id={id} photoData={photoData} proxy={proxy} webRuntimeData={webRuntimeData} />);
  };

  const remove = () => {
    proxy.photoUnlink(id, photoId);
    dialog.hide();
  };

  return <div className="photopreview">
    <div className="preview">
      <div className="switch">
        <Button
          onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)}
          disabled={currentPhotoIndex === 0}
          >
          &lt;
        </Button>
      </div>
      <div className="photo">
        <img src={imageUrl} alt="Phoenix Photo Preview" />
      </div>
      <div className="switch">
        <Button
          onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)}
          disabled={currentPhotoIndex === (photoData.photos.length - 1)}
          >
          &gt;
        </Button>
      </div>
    </div>
    <ButtonRow>
      <Button
          raised
          primary
          onClick={addAnother}
        >
          Add Another
        </Button>
        <Button
          raised
          onClick={remove}
        >
          Remove
        </Button>
    </ButtonRow>
  </div>;
};

export const PhoenixPhotoReadOnlyPreview = ({
  photoData,
  webRuntimeData,
} : WizardProps) => {

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);

  const photoId = photoData.photos[currentPhotoIndex].id;
  const imageUrl = `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/photos/${photoId}`;

  return <div className="photopreview">
    <div className="preview">
      <div className="switch">
        <Button
          onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)}
          disabled={currentPhotoIndex === 0}
          >
          &lt;
        </Button>
      </div>
      <div className="photo">
        <img src={imageUrl} alt="Phoenix Photo Preview" />
      </div>
      <div className="switch">
        <Button
          onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)}
          disabled={currentPhotoIndex === (photoData.photos.length - 1)}
          >
          &gt;
        </Button>
      </div>
    </div>
    <ButtonRow />
  </div>;
};

const Chooser = ({
  id,
  photoData,
  proxy,
  webRuntimeData,
} : WizardProps) => {

  const dialog  = usePhoenixFormsDialog();

  type FILTER = "UNLINKED" | "ALL";

  const [selectedFilter, setSelectedFilter] = useState<FILTER>("ALL");
  const [selectedPhotoId, setSelectedPhotoId] = useState<string>(null);
  const uploader = useRef<HTMLInputElement>(null);

  const commitSelectedPhoto = () => {
    proxy.photoLink(id, selectedPhotoId);
    dialog.hide();
  };

  const addNew = () => {
    uploader.current.value = "";
    uploader.current.click();
  };

  const uploaderOnChange = () => {
    if (!uploader.current.files.length) return;    
    const file = uploader.current.files[0];    
    if (file.type.indexOf("image/") !== 0) return;

    dialog.hide();
    dialog.showLoader("Uploading Photo...");

    const xmlhttp = new XMLHttpRequest();
    xmlhttp.open("POST",  `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/photos`, true);

    let complete = false;
    xmlhttp.onreadystatechange = () => {
      if (xmlhttp.readyState === 4 && !complete) {
          complete = true;

          if (xmlhttp.status !== 0) {
            dialog.hide();

            if (xmlhttp.status === 200) {
              const meta = JSON.parse(xmlhttp.responseText);
              proxy.photoAdd(meta);
              proxy.photoLink(id, meta.id);
            }
            else {
              dialog.showError("Error uploading photo");
            }
          }
      }
    };

    xmlhttp.send(file);
  };

  const noPhotoAvailable = () => {
    dialog.hide();
    dialog.show(<NoPhotAvailable id={id} photoData={photoData} proxy={proxy} webRuntimeData={webRuntimeData} />);
  };

  const photos = selectedFilter === "ALL" ? photoData.allPhotos : photoData.allPhotos.filter(p => !p.links || !p.links.length);

  const options = [
    { label: "Unlinked", value: "UNLINKED" },
    { label: "All", value: "ALL" },
  ];

  return <div className="photochooser">
    <input 
      type="file" 
      className="uploader" 
      accept="image/*"
      ref={uploader}
      onChange={uploaderOnChange}
    />
    <div className="header">
      <MSelect
        defaultValue={options[1]}
        options={options}
        onChange={option => {
          setSelectedFilter(option.value);
          setSelectedPhotoId(null);
        }}
      />
    </div>
    <div className="content">
      {photos.map(p => <ChooserPhoto
        key={p.id}
        photo={p}
        selected={p.id === selectedPhotoId}
        onClick={() => setSelectedPhotoId(p.id)}
        webRuntimeData={webRuntimeData}
      />)}
    </div>
    <ButtonRow>
      <Button
          raised
          primary
          disabled={selectedPhotoId === null}
          onClick={commitSelectedPhoto}
      >
        Select
      </Button>
      <Button
          raised
          primary
          onClick={addNew}
      >
        Add New
      </Button>
      {photoData.photos.length === 0 &&
        <Button
            raised          
            onClick={noPhotoAvailable}
        >
          No Photo Available
        </Button>
      }
    </ButtonRow>
  </div>;
};

interface ChooserPhotoProps {
  photo: models.PhotoMetadata;
  selected: boolean;
  onClick: () => void;
  webRuntimeData: appData.WebRuntimeAppData;
}
const ChooserPhoto = ({
  photo,
  selected,
  onClick,
  webRuntimeData,
} : ChooserPhotoProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isInView, setIsInView] = useState<boolean>(false);
  const [imgLoaded, setImgLoaded] = useState<boolean>(false);
  
  const imgClassName = `${imgLoaded ? "loaded" : ""} ${selected ? "selected" : ""}`;

  useEffect(() => {
    // lazy loading implementation
    setIsInView(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [photo]);
  
  return <img 
    className={imgClassName}
    src={isInView ? `${webRuntimeData.host}/services/forms4/${webRuntimeData.fileId}/phoenix-editor/${webRuntimeData.sessionKey}/photos/${photo.id}` : ""} 
    alt={photo.id} 
    onClick={onClick}
    ref={imgRef}
    onLoad={() => setImgLoaded(true)}
  />;
};

const NoPhotAvailable = ({
  id,
  photoData,
  proxy,
  webRuntimeData,
} : WizardProps) => {

  const dialog  = usePhoenixFormsDialog();

  const [reason, setReason] = useState<string>(null);
  const [description, setDescription] = useState<string>(null);

  const options = [
    { label: "Not allowed", value: "Not allowed"},
    { label: "Subject not available", value: "Subject not available"},
    { label: "No access", value: "No access"},
    { label: "Other", value: "Other"},
  ];

  const submit = () => {
    if (!reason || !reason.length) return;
    if (!description || !description.length) return;

    proxy.photoNotAvailable(id, reason, description);
    dialog.hide();
  };

  return <div className="no-photo">
    <div className="content">
      <label htmlFor="no-photo-reason">Reason no photo was available:</label>
      <MSelect
        id="no-photo-reason"
        options={options}
        onChange={v => setReason(v.value)}
      />
      <label htmlFor="no-photo-description">Description:</label>
      <TextField
        id="no-photo-description"
        onChange={(newValue) => { setDescription(`${newValue}`); }}
        placeholder="Enter Value..."
        rows={4}
      />
    </div>
    <ButtonRow>
      <Button
        raised
        primary
        onClick={submit}>
          Submit
      </Button>
    </ButtonRow>
  </div>;
};

const PhoenixPhotoWizard = ({
  id,
  photoData,
  proxy,
  webRuntimeData,
} : WizardProps) => {


  return photoData.photos.length ? 
    <Preview id={id} photoData={photoData} proxy={proxy} webRuntimeData={webRuntimeData} /> :
    <Chooser id={id} photoData={photoData} proxy={proxy} webRuntimeData={webRuntimeData} />;
};

export default PhoenixPhotoWizard;